import type { PerfLabOffer } from './types';

/**
 * Festive Lab promo, 23 Oct 2026 00:00 IST through 10 Nov 2026 23:59 IST.
 *
 * Shopify list prices stay full. Cards show strike + rounded 15% offer;
 * checkout attaches `EXINPL15`. Amounts are locked from Storefront
 * list + cartCreate-with-code (see scripts/audit-pl-exinpl15-prices.mjs),
 * not a live × 0.85, so display cannot drift from Shopify rounding.
 */
export const LAB_PRICE_CUT_STARTS = '2026-10-23T00:00:00+05:30';
export const LAB_PRICE_CUT_ENDS = '2026-11-10T23:59:59+05:30';
export const LAB_PRICE_CUT_DISCOUNT_CODE = 'EXINPL15';

type PriceCutRow = {
  /** Shopify list price, shown struck through. */
  strikePrice: number;
  /** Rounded cart total with EXINPL15 applied. */
  offerPrice: number;
};

/**
 * Catalogue plan ids only. GLP program-only rows are separate: the medication
 * tier reuses the same plan id and must not pick up the program price.
 */
const CATALOGUE_PRICE_CUT: Record<string, PriceCutRow> = {
  'diagnostics-basic': { strikePrice: 9999, offerPrice: 8499 },
  'diagnostics-pro': { strikePrice: 29999, offerPrice: 25499 },
  'diagnostics-elite': { strikePrice: 69999, offerPrice: 59499 },
  'gold-pro': { strikePrice: 89999, offerPrice: 76499 },
  'gold-elite': { strikePrice: 129999, offerPrice: 110499 },
  'pl-addon-vo2max-treadmill': { strikePrice: 7499, offerPrice: 6374 },
  'pl-addon-vo2max-bike': { strikePrice: 7499, offerPrice: 6374 },
  'pl-addon-dexa': { strikePrice: 3499, offerPrice: 2974 },
  'pl-addon-genome-health': { strikePrice: 35999, offerPrice: 30599 },
  'pl-addon-gut-microbiome': { strikePrice: 7999, offerPrice: 6799 },
  'pl-addon-nutrition-pro': { strikePrice: 24999, offerPrice: 21249 },
  'pl-addon-eye-scan': { strikePrice: 3499, offerPrice: 2974 },
  'recovery-day-pass': { strikePrice: 1999, offerPrice: 1699 },
  'recovery-month-pass': { strikePrice: 9999, offerPrice: 8499 },
  'recovery-3-month-pass': { strikePrice: 15999, offerPrice: 13599 },
  'pl-standalone-hbot': { strikePrice: 9999, offerPrice: 8499 },
  'pl-standalone-massage-male': { strikePrice: 2499, offerPrice: 2124 },
  'pl-standalone-massage-female': { strikePrice: 2499, offerPrice: 2124 },
  'pl-standalone-cryo': { strikePrice: 3999, offerPrice: 3399 },
  'iv-miniboost': { strikePrice: 7900, offerPrice: 6715 },
  'iv-hydromax': { strikePrice: 9900, offerPrice: 8415 },
  'iv-ultraviv': { strikePrice: 12900, offerPrice: 10965 },
  'iv-10x-health': { strikePrice: 13900, offerPrice: 11815 },
  'iv-megaboost': { strikePrice: 19900, offerPrice: 16915 },
  'iv-vitaglow': { strikePrice: 19900, offerPrice: 16915 },
  'iv-nad-125': { strikePrice: 29900, offerPrice: 25415 },
  'iv-ultraviv-pro': { strikePrice: 34900, offerPrice: 29665 },
  'iv-glutathione': { strikePrice: 7000, offerPrice: 5950 },
  'pl-im-b-family': { strikePrice: 2900, offerPrice: 2465 },
  'pl-im-vitamin-c': { strikePrice: 2900, offerPrice: 2465 },
  'pl-im-vitamin-d': { strikePrice: 2900, offerPrice: 2465 },
  'pl-im-vitaboost': { strikePrice: 3900, offerPrice: 3315 },
  'pl-im-nad': { strikePrice: 11900, offerPrice: 10115 },
};

const GLP_PROGRAM_PRICE_CUT: Record<string, PriceCutRow> = {
  'glp-1-month': { strikePrice: 19999, offerPrice: 16999 },
  'glp-6-month': { strikePrice: 99999, offerPrice: 84999 },
};

/**
 * Gift-only SKUs (not on the buy catalogue). Strike = Shopify list;
 * offer = rounded cart with EXINPL15 (audit 23 Oct 2026).
 */
export const GIFT_ONLY_PRICE_CUT: Record<string, PriceCutRow> = {
  'membership-monthly': { strikePrice: 22999, offerPrice: 19549 },
  'membership-annual': { strikePrice: 199999, offerPrice: 169999 },
  'cryo-10': { strikePrice: 39990, offerPrice: 33992 },
  'cryo-20': { strikePrice: 69980, offerPrice: 59483 },
  'hbot-10': { strikePrice: 59990, offerPrice: 50992 },
  'hbot-20': { strikePrice: 99980, offerPrice: 84983 },
  'massage-male-month': { strikePrice: 11897, offerPrice: 10112 },
  'massage-female-month': { strikePrice: 11897, offerPrice: 10112 },
  'massage-male-3-month': { strikePrice: 44787, offerPrice: 38069 },
  'massage-female-3-month': { strikePrice: 44787, offerPrice: 38069 },
  'hydromax-4': { strikePrice: 33660, offerPrice: 28611 },
  'vitaglow-4': { strikePrice: 67660, offerPrice: 57511 },
  'megaboost-4': { strikePrice: 67660, offerPrice: 57511 },
  'nad-4': { strikePrice: 101660, offerPrice: 86411 },
};

const toOffer = (row: PriceCutRow): PerfLabOffer => ({
  offerPrice: row.offerPrice,
  strikePrice: row.strikePrice,
  startsAt: LAB_PRICE_CUT_STARTS,
  expiresAt: LAB_PRICE_CUT_ENDS,
  discountCode: LAB_PRICE_CUT_DISCOUNT_CODE,
  badgeLabel: '15% OFF',
});

export const isLabPriceCutWindow = (now = Date.now()): boolean => {
  if (now < new Date(LAB_PRICE_CUT_STARTS).getTime()) return false;
  if (now >= new Date(LAB_PRICE_CUT_ENDS).getTime()) return false;
  return true;
};

export const isLabPriceCutEnded = (now = Date.now()): boolean =>
  now >= new Date(LAB_PRICE_CUT_ENDS).getTime();

/** Price cut for a buy-page catalogue plan. Null when the plan is not in the map. */
export const getCataloguePriceCutOffer = (planId: string): PerfLabOffer | null => {
  const row = CATALOGUE_PRICE_CUT[planId];
  return row ? toOffer(row) : null;
};

/**
 * Program-only GLP cut. The medication tier shares the plan id, so it must not
 * go through `getCataloguePriceCutOffer`.
 */
export const getGlpProgramPriceCutOffer = (planId: string): PerfLabOffer | null => {
  const row = GLP_PROGRAM_PRICE_CUT[planId];
  return row ? toOffer(row) : null;
};

/** Gift-only festive sub-plan id → offer while the Lab cut is active. */
export const getGiftOnlyPriceCutOffer = (subPlanId: string): PerfLabOffer | null => {
  const row = GIFT_ONLY_PRICE_CUT[subPlanId];
  return row ? toOffer(row) : null;
};
